import * as THREE from 'three';
import { Room } from './Room.js';
import { ROOMS } from '../data/rooms/index.js';
import { NPC } from '../entities/NPC.js';
import { EntityManager } from '../entities/EntityManager.js';
import { TransitionOverlay } from '../ui/TransitionOverlay.js';
import { EventBus } from '../core/EventBus.js';
import { Engine } from '../core/Engine.js';

export class RoomManager {
  constructor(scene) {
    this.mainScene = scene;
    this.currentRoomId = null;
    this.currentRoom = null;
    this.entityManager = new EntityManager();
    this.transition = new TransitionOverlay();
    this.roomGroup = null; // The THREE.Group for current room
  }

  // Build a room from data
  _buildRoom(roomId, flags = {}) {
    const data = ROOMS[roomId];
    if (!data) {
      console.error(`Room not found: ${roomId}`);
      return null;
    }
    const room = new Room(data);
    room.build(flags);
    return room;
  }

  // Load initial room (no transition)
  loadRoom(roomId, spawnX, spawnZ, flags = {}) {
    this._clearCurrentRoom();

    const room = this._buildRoom(roomId, flags);
    if (!room) return null;

    this.currentRoomId = roomId;
    this.currentRoom = room;
    // NOTE: no `this.rooms[roomId] = room` cache. It was write-only (nothing
    // ever read it) and it pinned every room ever visited in memory, so even
    // with dispose() wired below the old geometries could never be collected.

    // Add room geometry to scene
    this.roomGroup = room.group;
    this.mainScene.add(this.roomGroup);

    // Per-room mood lighting (falls back to default office rig)
    Engine.applyRoomLighting(room.data.lighting);

    // Interior lighting design layer: ceiling light pools, wall-seam AO
    // frame, furniture contact blobs, floor gloss + neon reflections
    Engine.applyRoomFX(room.data, this.roomGroup);

    // Rebuild the ghost building shell around this room (handles the
    // first-load race before the lazy module resolves). The shell returns
    // the canonical plate center — recenter the city ring on it so the
    // skyline stays anchored to the same building across floors.
    if (Engine.buildingShell) {
      const plateCenter = Engine.buildingShell.buildFor(room.data);
      if (plateCenter) Engine.cityBackdrop?.setCenter(plateCenter.x, plateCenter.z);
    } else {
      Engine._pendingShellRoom = room.data;
    }

    // Set up NPCs
    this.entityManager.clear();
    const npcData = room.getNPCData();
    for (const npc of npcData) {
      // Build conditionFn from data-driven condition object
      let conditionFn = npc.conditionFn || null;
      if (npc.condition) {
        const cond = npc.condition;
        conditionFn = (flags) => {
          if (cond.flag && !flags[cond.flag]) return false;
          if (cond.notFlag && flags[cond.notFlag]) return false;
          return true;
        };
      }
      const npcEntity = new NPC(npc.id, npc.x, npc.z, {
        facing: npc.facing || 0,
        dialogId: npc.dialogId || npc.id,
        visible: conditionFn ? false : npc.visible !== false,
        interactable: npc.interactable !== false,
        sitting: npc.sitting || false,
        movement: npc.movement || null,
        interactRange: npc.interactRange,
        conditionFn,
      });
      npcEntity.tileMap = room.tileMap;
      this.entityManager.addNPC(npcEntity);
      this.mainScene.add(npcEntity.mesh);
    }

    // The whole shadow-caster set just changed. The renderer's shadow map is
    // on a manual cadence (Engine.init) — tell it the room is new so the first
    // frame of the room is never lit by the previous room's shadows.
    Engine.invalidateShadows();

    EventBus.emit('room-entered', roomId);
    return { tileMap: room.tileMap, spawnX: spawnX ?? room.data.playerSpawn?.x ?? 5, spawnZ: spawnZ ?? room.data.playerSpawn?.z ?? 5 };
  }

  // Transition to a new room
  async changeRoom(targetRoomId, spawnX, spawnZ) {
    await this.transition.fadeOut(0.3);

    const result = this.loadRoom(targetRoomId, spawnX, spawnZ);

    await this.transition.fadeIn(0.3);

    return result;
  }

  _clearCurrentRoom() {
    if (this.roomGroup) {
      this.mainScene.remove(this.roomGroup);
      this.roomGroup = null;
    }
    // Release the outgoing room's GPU resources. scene.remove() alone only
    // unparents the group — the geometries stay resident, which grew the
    // geometry count by ~305 per room hop with no ceiling. Room.dispose()
    // owns the shared-vs-room-owned rules.
    if (this.currentRoom) {
      this.currentRoom.dispose();
      this.currentRoom = null;
    }

    // Remove NPC meshes — and free them. Every NPC is rebuilt from scratch by
    // the next loadRoom(), so nothing here survives the transition. Character
    // GEOMETRY is per-instance; character MATERIALS and face/hair/cloth
    // TEXTURES come out of MaterialLibrary / FacePainter / CharacterBuilder
    // caches and are shared, so they are deliberately left alone.
    this.entityManager.removeFromScene(this.mainScene);
    for (const npc of this.entityManager.npcs) {
      npc.mesh?.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
      });
    }
    this.entityManager.clear();
    this.currentRoom = null;
  }

  getCurrentTileMap() {
    return this.currentRoom ? this.currentRoom.tileMap : null;
  }

  getRoomData(roomId) {
    return ROOMS[roomId] || null;
  }

  update(dt, flags, paused) {
    this.entityManager.update(dt, flags, paused);
  }
}
